// The remediation console. Pick an action, pick an entity the server offered,
// read what would change, type the confirmation, apply.
//
// Every rule this screen appears to enforce is enforced again on the server:
// the verdict is recomputed on apply, the confirmation is compared against the
// server's own spelling, and the UPDATE carries the preflight's predicates in
// its WHERE. Nothing here is load-bearing — it exists so an operator finds out
// before they type rather than after they click.
//
// The decisions that are *testable* live in `lib/remediation-view.ts`, as pure
// functions; this file only renders them.

import { useId, useState } from 'react';
import {
  useApplyRemediation,
  usePreviewRemediation,
  useRemediationCandidates,
} from '@/api/config-hooks';
import type {
  RemediationAction,
  RemediationApplyResponse,
  RemediationCandidate,
} from '@/api/manage-types';
import { MutationErrorLine } from '@/components/error-panel';
import {
  ACTION_COPY,
  canApply,
  candidateKey,
  candidatesFor,
  confirmationState,
  isReady,
  outcomeMessageFor,
  REMEDIATION_ACTIONS,
  verdictLabel,
} from '@/lib/remediation-view';

const ACTIONS = REMEDIATION_ACTIONS;

/** Where the screen is, as the route's search params carry it. */
export interface RemediationsLocation {
  action: RemediationAction;
  entity: string | undefined;
}

export interface RemediationsViewProps {
  action: RemediationAction;
  /** A candidate key from the URL; validated against the fetched list, not trusted. */
  entity: string | undefined;
  onNavigate: (next: RemediationsLocation) => void;
}

export function RemediationsView({ action, entity, onNavigate }: RemediationsViewProps) {
  const candidates = useRemediationCandidates();
  const preview = usePreviewRemediation();
  const apply = useApplyRemediation();
  const confirmInputId = useId();
  const [typed, setTyped] = useState('');
  const [applied, setApplied] = useState<RemediationApplyResponse | null>(null);

  const rows = candidatesFor(candidates.data?.candidates, action);
  const selected = rows.find((row) => candidateKey(row) === entity) ?? null;
  const plan = preview.data;
  const ready = isReady(plan);
  const confirm = confirmationState(plan, typed);

  const choose = async (candidate: RemediationCandidate) => {
    setTyped('');
    setApplied(null);
    apply.reset();
    onNavigate({ action, entity: candidateKey(candidate) });
    await preview.mutateAsync({ action, target: candidate.target });
  };

  const run = async () => {
    // Apply renders only beside a plan for a selected row; this is the same
    // condition, stated where the compiler can see it.
    if (!selected || !plan) return;
    const result = await apply.mutateAsync({
      action,
      target: selected.target,
      state_fingerprint: plan.state_fingerprint,
      confirmation: typed,
    });
    setApplied(result);
    setTyped('');
    preview.reset();
    await candidates.refetch();
  };

  return (
    // The admin page frame (Audit log, Roles): the title in the sticky header
    // bar, the body a padded scroll region. The body's side padding is the
    // adaptive gutter where one is defined (≤1279 px: 20 px, phones 16 px, the
    // same inset `.page-hd` takes there) and the desktop 22 px above that.
    <div className="tbl-frame">
      <div className="page-hd">
        <div>
          <h1 className="page-title">Remediations</h1>
        </div>
      </div>

      <div
        role="region"
        aria-label="Remediations"
        tabIndex={0}
        style={{ flex: 1, overflow: 'auto', padding: '20px var(--adaptive-gutter, 22px) 60px' }}
      >
        <div className="muted" style={{ fontSize: 12, marginBottom: 20, maxWidth: 640, lineHeight: 1.5 }}>
          One-shot repairs for states the system cannot leave on its own. Each one is
          previewed first and applied against the state you reviewed — if the entity moves
          in between, the repair declines rather than overwriting it.
        </div>

        <div className="field-lbl mb-2">Repair</div>
        <div className="flex" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {ACTIONS.map((id) => (
            <button
              key={id}
              type="button"
              className={`btn btn-sm${id === action ? '' : ' btn-ghost'}`}
              aria-pressed={id === action}
              onClick={() => {
                setTyped('');
                setApplied(null);
                apply.reset();
                preview.reset();
                onNavigate({ action: id, entity: undefined });
              }}
            >
              {ACTION_COPY[id].label}
            </button>
          ))}
        </div>
        <div className="muted" style={{ fontSize: 11.5, marginBottom: 16, lineHeight: 1.5 }}>
          {ACTION_COPY[action].blurb}
        </div>

        <div className="field-lbl mb-2">
          Entities
          <span className="muted mono" style={{ fontSize: 10, marginLeft: 8 }}>({rows.length})</span>
        </div>
        <div className="panel" style={{ padding: 0, marginBottom: 16 }}>
          {candidates.isPending ? (
            <div className="muted" style={{ padding: 12, fontSize: 12 }}>Looking…</div>
          ) : rows.length === 0 ? (
            // Not an error, and worth saying plainly: an empty list is the
            // system reporting that nothing is stuck.
            <div className="muted" style={{ padding: 12, fontSize: 12 }}>
              Nothing is currently in this state.
            </div>
          ) : (
            rows.map((row) => {
              const key = candidateKey(row);
              return (
                <button
                  key={key}
                  type="button"
                  className="flex between items-center"
                  aria-pressed={key === entity}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: key === entity ? 'var(--bg-sunk)' : 'none',
                    border: 0,
                    borderBottom: '1px solid var(--line)',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onClick={() => void choose(row)}
                >
                  <span className="mono" style={{ fontSize: 11.5 }}>{row.label}</span>
                  <span className="muted" style={{ fontSize: 11 }}>{row.detail}</span>
                </button>
              );
            })
          )}
        </div>

        {preview.isPending && (
          <div className="muted" style={{ fontSize: 12 }}>Checking…</div>
        )}
        <MutationErrorLine error={preview.error} />

        {plan && selected && (
          <>
            <div className="field-lbl mb-2">What would happen</div>
            <div className="panel" style={{ padding: 12, marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: ready ? 'var(--good)' : 'var(--warn)',
                }}
              >
                {verdictLabel(plan.verdict)}
              </div>
              {/* The sentence, always — including when ready. "Nothing is wrong"
                  and "we did not check" must not look alike. */}
              <div className="muted" style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.5 }}>
                {plan.reason}
              </div>
              {plan.changes.length > 0 && (
                <div style={{ marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
                  {plan.changes.map((change) => (
                    <div
                      key={`${change.table}.${change.column}`}
                      className="mono"
                      style={{ fontSize: 11, padding: '2px 0' }}
                    >
                      <span className="muted">{change.table}.{change.column}</span>{' '}
                      {change.before} <span className="muted">→</span> <b>{change.after}</b>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {ready && (
              <>
                <div className="field-lbl mb-2">Confirm</div>
                <div className="panel" style={{ padding: 12, marginBottom: 16 }}>
                  {/* A <label>, so the input below has an accessible name; `block`
                      keeps the look of the <div> it replaced. */}
                  <label
                    htmlFor={confirmInputId}
                    className="muted"
                    style={{ display: 'block', fontSize: 11.5, lineHeight: 1.5 }}
                  >
                    Type this exactly:
                  </label>
                  <div className="mono" style={{ fontSize: 12, margin: '6px 0 10px' }}>
                    {plan.confirmation}
                  </div>
                  <input
                    id={confirmInputId}
                    className="input mono"
                    style={{
                      width: '100%',
                      fontSize: 12,
                      borderColor:
                        confirm === 'mismatch' ? 'var(--warn)'
                          : confirm === 'match' ? 'var(--good)' : undefined,
                    }}
                    value={typed}
                    spellCheck={false}
                    autoComplete="off"
                    onChange={(event) => setTyped(event.target.value)}
                    placeholder="…"
                  />
                  <div className="flex between items-center" style={{ marginTop: 10 }}>
                    <span className="muted" style={{ fontSize: 11 }}>
                      {confirm === 'mismatch' ? 'Not an exact match yet.' : ' '}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={!canApply(plan, typed, apply.isPending)}
                      onClick={() => void run()}
                    >
                      {apply.isPending ? 'Applying…' : 'Apply'}
                    </button>
                  </div>
                </div>
              </>
            )}
            <MutationErrorLine error={apply.error} />
          </>
        )}

        {applied && (
          <div className="panel" style={{ padding: 12 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600 }}>
              {outcomeMessageFor(action, applied.steps)}
            </div>
            {/* Per statement, because the TTD release writes two journal copies
                that are not atomic together. */}
            {applied.steps.map((step) => (
              <div key={step.name} className="mono" style={{ fontSize: 11, marginTop: 4 }}>
                {step.applied ? '✓' : '·'} {step.name} — {step.rows_affected} row
                {step.rows_affected === 1 ? '' : 's'}
              </div>
            ))}
            <div className="muted" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.5 }}>
              {applied.next}
            </div>
            <div className="muted mono" style={{ fontSize: 10.5, marginTop: 6 }}>
              audit {applied.audit_id}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
